<template>
  <jet-action-section>
    <template #title>
      Two factor authentication
      <span v-if="twoFactorAuthenticatorEnabled || twoFactorMultiOTPEnabled">enabled</span>
      <span
        v-if="!twoFactorAuthenticatorEnabled && !twoFactorMultiOTPEnabled"
        class="italic text-vice-red"
      >
        not enabled
      </span>
    </template>

    <template #description>
      <div class="mb-8 w-2/3">
        <span v-if="twoFactorAuthenticatorEnabled || twoFactorMultiOTPEnabled">
          You
          <span class="font-bold">have enabled</span>
          two-factor authentication
          <span v-show="twoFactorAuthenticatorEnabled">using the Google Authenticator App</span>
          <span v-show="twoFactorMultiOTPEnabled">
            via the One Time Password method using SMS and Email confirmation codes.
          </span>
        </span>

        <span v-else-if="twoFactorAuthenticatorIntended">
          Follow these steps to set up Google Authenticator for two-factor authentication and add
          <br />
          an extra layer of security to your account:
        </span>

        <span v-else-if="twoFactorMultiOTPIntended">
          Follow these steps to set up One-Time Password (OTP) for two-factor authentication,
          <br />
          adding an extra layer of security to your account.
        </span>

        <span v-else>
          You
          <span class="font-bold">haven't enabled</span>
          two-factor authentication yet.
          <br />
          Please choose one of the following methods to secure your account:
        </span>
      </div>
    </template>

    <template #content>
      <div>
        <!-- DISABLED -->
        <div class="mt-3 text-sm text-gray-600 w-full" v-if="twoFactorDisabled">
          <div class="mt-8 grid grid-rows-2 grid-cols-3 gap-8 items-center">
            <!-- First Option -->
            <div class="col-span-2">
              <div class="font-bold text-gray-700">1. Google Authenticator App</div>
              <div class="text-gray-700 pr-20">
                If you choose Google Authenticator App, you can generate a token using the Google
                Authenticator app on your phone.
              </div>
            </div>

            <div class="col-span-1 ml-4">
              <jet-confirms-password @confirmed="enableTwoFactorAuthentication">
                <ViceButton
                  data-cy="enable-tfa"
                  class="px-4 border border-vice-light-grey"
                  :class="{ 'opacity-25': enabling }"
                  :disabled="enabling"
                  medium
                >
                  Enable Authenticator
                </ViceButton>
              </jet-confirms-password>
            </div>
            <!-- Second Option -->
            <div class="col-span-2">
              <div class="font-bold text-gray-700">2. One-Time Password (OTP)</div>
              <div class="text-gray-700 pr-20">
                If you opt for One-Time Password (OTP), a code will be sent to you via SMS and
                email.
              </div>
            </div>
            <div class="col-span-1 ml-4">
              <jet-confirms-password @confirmed="enableOneTimeAuthentication">
                <ViceButton
                  data-cy="enable-ota"
                  class="px-4 border border-vice-light-grey"
                  :class="{ 'opacity-25': enabling }"
                  :disabled="enabling"
                  medium
                >
                  Enable OTP
                </ViceButton>
              </jet-confirms-password>
            </div>
          </div>
        </div>

        <!-- INTENDED via Authenticator App -->
        <div v-if="twoFactorAuthenticatorIntended">
          <div class="grid sm:grid-cols-3 grid-cols-2 gap-10 items-center pb-4">
            <!-- Part 1 -->
            <div class="col-span-2">
              <div class="font-bold text-gray-700">1. Download Google Authenticator</div>
              <div class="text-gray-700 pr-20">
                If you haven't already, download the Google Authenticator app from the Apple App
                Store or Google Play Store.
              </div>
            </div>
            <!-- Part 2 -->
            <div class="col-span-2">
              <div class="font-bold text-gray-700">2. Scan the QR code</div>
              <div class="text-gray-700 pr-20">
                Open the Google Authenticator app on your phone and tap the “+” button to add a new
                account. Then, scan the QR code below to link your account.
              </div>
              <div v-if="qrCode">
                <div class="mt-8" v-html="qrCode"></div>
              </div>
            </div>
            <!-- Part 3 -->
            <div class="col-span-2">
              <div class="font-bold text-gray-700">3. Enter the code and confirm</div>
              <div class="text-gray-700 pr-20">
                Enter the 6-digit code from the Google Authenticator app and click “Enable
                Authenticator” to enable two-factor authentication. If the code is correct, your
                account will be secured.
              </div>
              <form @submit.prevent="confirm2faCode('google_authenticator')">
                <div class="mt-4 inline-flex w-full gap-6 items-end">
                  <div class="flex-1 w-2/3">
                    <jet-label for="code" value="Confirm Google Authenticator code" />
                    <jet-input
                      required
                      id="code"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      title="6-digit code"
                      type="text"
                      ref="confirmation_code"
                      v-model="form.code"
                      class="mt-1 block w-full h-9 leading-4"
                    />
                  </div>
                  <ViceButton
                    data-cy="confirm-2fa"
                    class="flex px-4 border border-vice-light-grey"
                    :class="{ 'opacity-25': enabling }"
                    :disabled="enabling"
                    medium
                  >
                    Enable Authenticator
                  </ViceButton>
                </div>
                <jet-input-error :message="this.form.errors.code" class="mt-1 absolute" />
              </form>
            </div>
          </div>
          <div class="flex mt-4 mb-2">
            <jet-secondary-button data-cy="cancel-2fa" @click="disableTwoFactorAuthentication">
              Cancel
            </jet-secondary-button>
          </div>
        </div>

        <!-- INTENDED via MultiOTP App -->
        <div v-if="twoFactorMultiOTPIntended">
          <div class="grid sm:grid-cols-3 grid-cols-2 gap-10 items-center pb-4">
            <!-- Part 1 -->
            <div class="col-span-2">
              <div class="font-bold text-gray-700">1. Enter or check your phone number</div>
              <div class="text-gray-700 pr-20">
                Enter your phone number to receive OTP, or verify that the number in your profile
                information is correct before proceeding. After that, we'll send a test OTP code via
                SMS and email for verification of your phone number.
              </div>
              <form @submit.prevent="sentOTP">
                <div class="mt-4 inline-flex w-full gap-6 items-end">
                  <div class="flex-1 w-full">
                    <jet-label for="phone" value="Phone number" />
                    <vue-tel-input
                      data-cy="otp-phone"
                      v-model="form.phone"
                      id="phone"
                      ref="phone"
                      class="mt-1 h-9"
                      :defaultCountry="'GB'"
                      :mode="'international'"
                      :ignoredCountries="['RU']"
                      :inputOptions="{ showDialCode: true }"
                      :disabled="phoneConfirmed"
                      @input="phoneConfirmed = false"
                    ></vue-tel-input>
                    <jet-input-error :message="this.form.errors.phone" class="mt-1 absolute" />
                  </div>
                  <ViceButton
                    data-cy="confirm-otp"
                    class="flex px-4 border border-vice-light-grey"
                    :class="{ 'opacity-25': confirming }"
                    :disabled="confirming || phoneConfirmed"
                    medium
                  >
                    Confirm
                  </ViceButton>
                  <div class="w-1/3 text-gray-800 text-sm font-semibold">
                    <span v-if="showConfirmMessage">
                      We have sent a verification code to your email/SMS
                    </span>
                  </div>
                </div>
              </form>
            </div>
            <!-- Part 2 -->
            <div class="col-span-2">
              <div class="font-bold text-gray-700">2. Enter OTP code and confirm phone number</div>
              <div class="text-gray-700 pr-20">
                Enter the OTP code sent to your email address or your phone via SMS and click
                “Verify and enable OTP’ to verify your phone number and enable two-factor
                authentication. If the code is correct, your account will be secured.
              </div>
              <form @submit.prevent="confirm2faCode('multi_channel_otp')">
                <div class="mt-4 inline-flex w-full gap-6 items-end">
                  <div class="flex-1 w-2/3">
                    <jet-label for="code" value="Verify OTP code" />
                    <jet-input
                      required
                      id="code"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      title="otp code, like 1234"
                      type="text"
                      ref="code"
                      v-model="form.code"
                      :disabled="!phoneConfirmed"
                      class="mt-1 block w-full h-9 leading-4"
                    />
                  </div>
                  <ViceButton
                    data-cy="confirm-otp"
                    class="flex px-4 border border-vice-light-grey"
                    :class="{ 'opacity-25': confirming }"
                    :disabled="confirming || !phoneConfirmed"
                    medium
                  >
                    Verify and enable OTP
                  </ViceButton>
                </div>
                <jet-input-error :message="this.form.errors.code" class="mt-1 absolute" />
              </form>
            </div>
          </div>
          <div class="flex mt-4 mb-2">
            <jet-secondary-button data-cy="cancel-2fa" @click="disableTwoFactorAuthentication">
              Cancel
            </jet-secondary-button>
          </div>
        </div>

        <!-- ENABLED via Authenticator App -->
        <div v-if="twoFactorAuthenticatorEnabled" class="mb-2">
          <div v-if="recoveryCodes.length > 0 && showCodes">
            <div class="grid sm:grid-cols-2 lg:grid-cols-3 lg:w-2/3 grid-cols-2 gap-8 pb-8">
              <div class="col-span-1 w-60">
                <div
                  class="px-3 py-4 font-mono bg-medium-grey rounded-lg text-center"
                  @click="copyToClipboard"
                >
                  <div class="code" v-for="code in recoveryCodes" :key="code">
                    {{ code }}
                  </div>
                </div>
              </div>
              <div class="col-span-2 w-5/6 md:pl-6 lg:min-lg:pl-7 h-full">
                <div class="content-center lg:h-1/2">
                  <span>
                    Download or write down these backup codes in a safe place. These codes can be
                    used to access your account if you lose access to your authenticator app.
                  </span>
                </div>
                <div class="content-center lg:h-1/2">
                  <ViceButton
                    @click="regenerateRecoveryCodes"
                    data-cy="regenerate-codes"
                    class="px-4 border border-vice-light-grey"
                    :class="{ 'opacity-25': enabling }"
                    :disabled="enabling"
                    medium
                  >
                    Regenerate backup codes
                  </ViceButton>
                </div>
              </div>
            </div>
          </div>

          <jet-secondary-button v-if="showCodes" @click="hideRecoveryCodes" class="mr-3">
            Hide Recovery Codes
          </jet-secondary-button>

          <jet-secondary-button v-else class="mr-3" @click="showRecoveryCodes">
            Show Recovery Codes
          </jet-secondary-button>

          <jet-confirms-password @confirmed="disableTwoFactorAuthentication">
            <jet-danger-button :class="{ 'opacity-25': disabling }" :disabled="disabling">
              Disable Authenticator
            </jet-danger-button>
          </jet-confirms-password>
        </div>

        <!-- ENABLED via MultiOTP -->
        <div v-if="twoFactorMultiOTPEnabled">
          <jet-confirms-password @confirmed="disableTwoFactorAuthentication">
            <jet-danger-button :class="{ 'opacity-25': disabling }" :disabled="disabling">
              Disable OTP
            </jet-danger-button>
          </jet-confirms-password>
        </div>
        <action-message :on="activated" :message="activated"></action-message>
      </div>
    </template>
  </jet-action-section>
</template>

<script>
import axios from 'axios';
import { VueTelInput } from 'vue-tel-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import JetInput from '@/Jetstream/Input.vue';
import JetLabel from '@/Jetstream/Label.vue';
import JetInputError from '@/Jetstream/InputError.vue';
import ActionMessage from '@/Jetstream/ActionMessage.vue';
import JetDangerButton from '@/Jetstream/DangerButton.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import JetActionSection from '@/Jetstream/ActionSection.vue';
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue';
import JetConfirmsPassword from '@/Jetstream/ClinicianConfirmsPassword.vue';

export default {
  components: {
    VueTelInput,
    JetInputError,
    ViceButton,
    ActionMessage,
    JetLabel,
    JetInput,
    JetActionSection,
    JetConfirmsPassword,
    JetDangerButton,
    JetSecondaryButton,
  },

  data() {
    return {
      activated: null,
      enabling: false,
      disabling: false,
      confirming: false,
      qrCode: null,
      showCodes: false,
      recoveryCodes: [],
      phoneConfirmed: false,
      showConfirmMessage: false,
      form: {
        code: '',
        phone: this.$page.props.user.phone,
        errors: {},
      },
      platformSettings: this.$page.props.platformSettings,
      twoFAisRequired: false,
    };
  },

  mounted() {
    this.twoFAisRequired = !!this.platformSettings.Authentication?.includes('2FA is required');
    this.disabling = this.twoFAisRequired;

    if (this.twoFactorAuthenticatorIntended || this.twoFactorAuthenticatorEnabled) {
      this.showQrCode();
      this.showRecoveryCodes();
    }
  },

  methods: {
    enableTwoFactorAuthentication() {
      this.enabling = true;
      this.$inertia.post(
        route('clinician-two-factor.enable'),
        { auth_type: 'google_authenticator' },
        {
          preserveScroll: true,
          onSuccess: () => Promise.all([this.showQrCode(), this.showRecoveryCodes()]),
          onFinish: () => {
            this.disabling = !!this.twoFAisRequired;
            this.enabling = false;
          },
        },
      );
    },

    enableOneTimeAuthentication() {
      this.enabling = true;
      this.$inertia.post(
        route('clinician-two-factor.enable'),
        { auth_type: 'multi_channel_otp' },
        {
          preserveScroll: true,
          onFinish: () => {
            this.disabling = false;
            this.enabling = false;
          },
        },
      );
    },

    showQrCode() {
      return axios.get(route('clinician-two-factor.qr-code')).then((response) => {
        this.qrCode = response.data.svg;
      });
    },

    async confirm2faCode(authType) {
      this.confirming = true;
      this.form.errors = {};

      await axios
        .post(route('clinician-two-factor.confirm'), { auth_type: authType, code: this.form.code })
        .then((response) => {
          if (response.data.status) {
            this.activated = 'Two factor authentication has been enabled.';
            this.$page.props.user.two_factor_confirmed = 1;
            this.showCodes = true;
          }
        })
        .catch((e) => {
          console.error(e.response.data.error);
          this.form.errors.code = e.response.data.error;
        });
      this.confirming = false;
    },

    async sentOTP() {
      this.confirming = true;
      this.form.errors = {};
      const validated = isValidPhoneNumber(this.form.phone);

      if (validated) {
        await axios
          .post(route('clinician-two-factor.challenge'), { phone: this.form.phone })
          .then((response) => {
            if (response.data.status === 1) {
              this.phoneConfirmed = true;
              this.showConfirmMessage = true;
              this.toggleCodeInputState();
            } else if (response.data.status === 0) {
              this.form.errors.phone = response.data.message;
            }
          })
          .catch((e) => {
            if (e.response.status === 422) {
              this.form.errors.phone = e.response.data.message;
            } else {
              console.error(e.response.data.error);
            }
          });
      } else {
        this.form.errors.phone = 'Invalid phone number';
      }
      this.confirming = false;
    },

    showRecoveryCodes() {
      return axios.get(route('clinician-two-factor.recovery-codes')).then((response) => {
        this.recoveryCodes = response.data;
        this.showCodes = true;
      });
    },

    hideRecoveryCodes() {
      this.showCodes = false;
    },

    regenerateRecoveryCodes() {
      axios.post(route('clinician-post-two-factor.recovery-codes')).then(() => {
        this.showRecoveryCodes();
      });
    },

    disableTwoFactorAuthentication() {
      if (this.twoFactorAuthenticatorEnabled || this.twoFactorMultiOTPEnabled) {
        this.disabling = true;
      }

      this.$inertia.post(
        route('clinician-two-factor.disable'),
        {},
        {
          preserveScroll: true,
          onSuccess: () => {
            this.form.code = '';
            this.form.errors = {};
            this.phoneConfirmed = false;

            if (this.disabling) {
              this.activated = 'Two factor authentication has been disabled.';
            }
            this.disabling = false;
          },
        },
      );
    },

    copyToClipboard() {
      try {
        const codes = document.querySelectorAll('.code');
        let collectedText = '';

        codes.forEach((el) => {
          collectedText += `${el.textContent.trim()}\n`;
        });

        navigator.clipboard.writeText(collectedText);
      } catch (e) {
        console.error('Cannot copy');
      }
    },

    toggleCodeInputState() {
      if (this.phoneConfirmed) {
        this.$refs.code.disabled = false;
        this.$nextTick(() => {
          this.$refs.code.focus();
        });
      } else {
        this.$refs.code.disabled = true;
      }
    },
  },

  computed: {
    twoFactorAuthenticatorIntended() {
      return (
        this.$page.props.user.auth_type === 'google_authenticator' &&
        this.$page.props.user.two_factor_confirmed === 0
      );
    },
    twoFactorAuthenticatorEnabled() {
      return (
        this.$page.props.user.auth_type === 'google_authenticator' &&
        this.$page.props.user.two_factor_confirmed === 1
      );
    },
    twoFactorMultiOTPIntended() {
      return (
        this.$page.props.user.auth_type === 'multi_channel_otp' &&
        this.$page.props.user.two_factor_confirmed === 0
      );
    },
    twoFactorMultiOTPEnabled() {
      return (
        this.$page.props.user.auth_type === 'multi_channel_otp' &&
        this.$page.props.user.two_factor_confirmed === 1
      );
    },
    twoFactorDisabled() {
      return (
        !this.twoFactorAuthenticatorEnabled &&
        !this.twoFactorMultiOTPEnabled &&
        !this.twoFactorAuthenticatorIntended &&
        !this.twoFactorMultiOTPIntended
      );
    },
  },

  watch: {
    'form.phone': {
      handler(newVal) {
        if (newVal) this.form.phone = this.form.phone.replace(/\s+/g, '');
      },
    },
  },
};
</script>
